import { Column, Row } from "@/components";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { transcript as transcriptUtil } from "@/lib/transcript";
import { VoiceAIOutreachAiSummaryJson } from "@/types";

const ClickableTimestamp = ({
  audioRef,
  timestamp,
}: {
  audioRef: React.RefObject<HTMLAudioElement>;
  timestamp: string;
}) => {
  const handleClick = () => {
    if (!audioRef.current) return;

    const timeInSeconds = transcriptUtil.convertTimestampToSeconds(timestamp);
    if (timeInSeconds === null) return;

    mixpanelUtils.trackButtonClick(
      "Transcript Timestamp Seek",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      { timestamp },
    );
    audioRef.current.currentTime = timeInSeconds;
    void audioRef.current.play();
  };

  return (
    <button
      className="cursor-pointer text-xs font-medium text-primary-900 hover:underline"
      onClick={handleClick}
      type="button"
    >
      {timestamp}
    </button>
  );
};

const BooleanBadge = ({ value }: { value: boolean }) => {
  return value ? (
    <span className="rounded-full border border-green-200 bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700">
      Yes
    </span>
  ) : (
    <span className="rounded-full border border-red-200 bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700">
      No
    </span>
  );
};

const FieldRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => {
  return (
    <Column className="gap-0.5">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-700">{value}</dd>
    </Column>
  );
};

const SectionCard = ({
  audioRef,
  children,
  title,
  timestamp,
}: {
  audioRef: React.RefObject<HTMLAudioElement>;
  children: React.ReactNode;
  title: string;
  timestamp?: string | null;
}) => {
  return (
    <Column className="gap-3 rounded-lg border border-slate-200 p-4">
      <Row className="items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        {timestamp && (
          <ClickableTimestamp audioRef={audioRef} timestamp={timestamp} />
        )}
      </Row>
      <dl className="flex flex-col gap-3">{children}</dl>
    </Column>
  );
};

export const StructuredVoiceAISummary = ({
  audioRef,
  summaryJson,
}: {
  audioRef: React.RefObject<HTMLAudioElement>;
  summaryJson: VoiceAIOutreachAiSummaryJson;
}) => {
  const {
    motivation,
    currentEmployment,
    payAlignment,
    shiftAvailability,
    locationAndCommute,
    drugAndBackgroundCheck,
    workExperiences,
    dynamicQuestions,
    closingNotes,
    overallSummary,
  } = summaryJson;

  // Helper to check if a section has any non-null non-timestamp fields.
  const hasContent = (
    section: Record<string, unknown>,
    ignoreKeys: string[] = ["timestamp"],
  ) =>
    Object.entries(section).some(
      ([key, val]) =>
        !ignoreKeys.includes(key) && val !== null && val !== undefined && val !== "",
    );

  return (
    <Column className="gap-6">
      {/* Overall Summary */}
      {overallSummary && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm leading-relaxed text-slate-700">
            {overallSummary}
          </p>
        </div>
      )}

      {/* Motivation */}
      {hasContent(motivation) && (
        <SectionCard
          audioRef={audioRef}
          title="Motivation"
          timestamp={motivation.timestamp}
        >
          {motivation.whyThisRole && (
            <FieldRow label="Why this role" value={motivation.whyThisRole} />
          )}
          {motivation.followUp && (
            <FieldRow label="Follow up" value={motivation.followUp} />
          )}
        </SectionCard>
      )}

      {/* Current Employment */}
      {hasContent(currentEmployment) && (
        <SectionCard
          audioRef={audioRef}
          title="Current Employment"
          timestamp={currentEmployment.timestamp}
        >
          {currentEmployment.isCurrentlyEmployed !== null && (
            <FieldRow
              label="Currently employed"
              value={
                <BooleanBadge value={currentEmployment.isCurrentlyEmployed} />
              }
            />
          )}
          {currentEmployment.company && (
            <FieldRow label="Company" value={currentEmployment.company} />
          )}
          {currentEmployment.title && (
            <FieldRow label="Title" value={currentEmployment.title} />
          )}
          {currentEmployment.tenure && (
            <FieldRow label="Tenure" value={currentEmployment.tenure} />
          )}
          {currentEmployment.currentPay && (
            <FieldRow label="Current pay" value={currentEmployment.currentPay} />
          )}
          {currentEmployment.reasonForChange && (
            <FieldRow
              label="Reason for change"
              value={currentEmployment.reasonForChange}
            />
          )}
        </SectionCard>
      )}

      {/* Pay Alignment */}
      {hasContent(payAlignment) && (
        <SectionCard
          audioRef={audioRef}
          title="Pay Alignment"
          timestamp={payAlignment.timestamp}
        >
          {payAlignment.desiredPay && (
            <FieldRow label="Desired pay" value={payAlignment.desiredPay} />
          )}
          {payAlignment.isAlignedWithRange !== null && (
            <FieldRow
              label="Aligned with range"
              value={<BooleanBadge value={payAlignment.isAlignedWithRange} />}
            />
          )}
          {payAlignment.notes && (
            <FieldRow label="Notes" value={payAlignment.notes} />
          )}
        </SectionCard>
      )}

      {/* Shift Availability */}
      {hasContent(shiftAvailability) && (
        <SectionCard
          audioRef={audioRef}
          title="Shift Availability"
          timestamp={shiftAvailability.timestamp}
        >
          {shiftAvailability.isFlexible !== null && (
            <FieldRow
              label="Flexible"
              value={<BooleanBadge value={shiftAvailability.isFlexible} />}
            />
          )}
          {shiftAvailability.preferredShifts && (
            <FieldRow
              label="Preferred shifts"
              value={shiftAvailability.preferredShifts}
            />
          )}
          {shiftAvailability.notes && (
            <FieldRow label="Notes" value={shiftAvailability.notes} />
          )}
        </SectionCard>
      )}

      {/* Location & Commute */}
      {hasContent(locationAndCommute) && (
        <SectionCard
          audioRef={audioRef}
          title="Location & Commute"
          timestamp={locationAndCommute.timestamp}
        >
          {locationAndCommute.city && (
            <FieldRow label="City" value={locationAndCommute.city} />
          )}
          {locationAndCommute.estimatedCommute && (
            <FieldRow
              label="Estimated commute"
              value={locationAndCommute.estimatedCommute}
            />
          )}
          {locationAndCommute.isCommuteAcceptable !== null && (
            <FieldRow
              label="Commute acceptable"
              value={
                <BooleanBadge value={locationAndCommute.isCommuteAcceptable} />
              }
            />
          )}
          {locationAndCommute.notes && (
            <FieldRow label="Notes" value={locationAndCommute.notes} />
          )}
        </SectionCard>
      )}

      {/* Drug & Background Check */}
      {hasContent(drugAndBackgroundCheck) && (
        <SectionCard
          audioRef={audioRef}
          title="Drug & Background Check"
          timestamp={drugAndBackgroundCheck.timestamp}
        >
          {drugAndBackgroundCheck.willingToComplete !== null && (
            <FieldRow
              label="Willing to complete"
              value={
                <BooleanBadge
                  value={drugAndBackgroundCheck.willingToComplete}
                />
              }
            />
          )}
          {drugAndBackgroundCheck.notes && (
            <FieldRow label="Notes" value={drugAndBackgroundCheck.notes} />
          )}
        </SectionCard>
      )}

      {/* Work Experiences */}
      {workExperiences.length > 0 && (
        <Column className="gap-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Recent Work Experiences
          </h3>
          <Column className="gap-2">
            {workExperiences.map((exp, index) => (
              <Column
                key={index}
                className="gap-2 rounded-lg border border-slate-200 p-4"
              >
                <Row className="items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">
                    {[exp.title, exp.company].filter(Boolean).join(" at ") ||
                      `Experience ${String(index + 1)}`}
                  </p>
                  {exp.timestamp && (
                    <ClickableTimestamp
                      audioRef={audioRef}
                      timestamp={exp.timestamp}
                    />
                  )}
                </Row>
                <dl className="flex flex-col gap-2">
                  {exp.tenure && (
                    <FieldRow label="Tenure" value={exp.tenure} />
                  )}
                  {exp.reasonForLeaving && (
                    <FieldRow
                      label="Reason for leaving"
                      value={exp.reasonForLeaving}
                    />
                  )}
                </dl>
              </Column>
            ))}
          </Column>
        </Column>
      )}

      {/* Dynamic Questions / Themes */}
      {dynamicQuestions.length > 0 && (
        <Column className="gap-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Themes / Questions
          </h3>
          <Column className="gap-2">
            {dynamicQuestions.map((dq, index) => (
              <Column
                key={index}
                className="gap-2 rounded-lg border border-slate-200 p-4"
              >
                <Row className="items-center justify-between">
                  <p className="text-sm font-medium italic text-slate-700">
                    {dq.question}
                  </p>
                  {dq.timestamp && (
                    <ClickableTimestamp
                      audioRef={audioRef}
                      timestamp={dq.timestamp}
                    />
                  )}
                </Row>
                {dq.answer && (
                  <p className="text-sm text-slate-600">{dq.answer}</p>
                )}
              </Column>
            ))}
          </Column>
        </Column>
      )}

      {/* Closing Notes */}
      {hasContent(closingNotes) && (
        <Column className="gap-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Closing Notes
          </h3>
          <Column className="gap-2 rounded-lg border border-slate-200 p-4">
            <Row className="items-center justify-between">
              <dl className="flex flex-col gap-3">
                {closingNotes.candidateSentiment && (
                  <FieldRow
                    label="Candidate sentiment"
                    value={closingNotes.candidateSentiment}
                  />
                )}
                {closingNotes.redFlags && (
                  <FieldRow label="Red flags" value={closingNotes.redFlags} />
                )}
              </dl>
              {closingNotes.timestamp && (
                <ClickableTimestamp
                  audioRef={audioRef}
                  timestamp={closingNotes.timestamp}
                />
              )}
            </Row>
          </Column>
        </Column>
      )}

    </Column>
  );
};
